/**
 * CraftingOverlay.js
 *
 * Phase 2 — Craft / Refining chains (Fabricator).
 *
 * Opened from a Refinery structure or the Fabricator terminal inside a ship
 * interior. Players refine raw ore into multi-tier alloys via timed jobs:
 * start a recipe → wait → collect. Alloys feed higher-tier recipes and the
 * higher Phase-1 ship-module tiers.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import {
  Factory, DoorOpen, Boxes, Cpu, Shield, Gem, Clock, Hammer, CheckCircle2,
  FlaskConical, Layers, Atom, Coins,
} from 'lucide-react';

const TIER_TONE = {
  1: 'border-slate-400/30 text-slate-200',
  2: 'border-cyan-400/40 text-cyan-200',
  3: 'border-fuchsia-400/40 text-fuchsia-200',
  4: 'border-amber-400/40 text-amber-200',
};
const RECIPE_ICON = { boxes: Boxes, cpu: Cpu, shield: Shield, gem: Gem, flask: FlaskConical, layers: Layers, atom: Atom };
const CAT_ICON = { boxes: Boxes, cpu: Cpu, flask: FlaskConical, layers: Layers, atom: Atom };

function fmtTime(s) {
  s = Math.max(0, Math.round(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

export default function CraftingOverlay({ open, api, source, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async (silent) => {
    try {
      const res = await api.get('/api/crafting/state');
      setData(res.data);
    } catch (_e) { /* noop */ } finally { if (!silent) setLoading(false); }
  }, [api]);

  useEffect(() => {
    if (!open) return undefined;
    refresh(false);
    pollRef.current = setInterval(() => refresh(true), 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, refresh]);

  const nameById = {};
  (data?.recipes || []).forEach((r) => { nameById[r.id] = r.name; });

  const canAfford = useCallback((recipe) => {
    if (!data) return false;
    for (const ing of recipe.inputs) {
      const have = ing.type === 'ore' ? (data.ore_counts[ing.key] || 0) : (data.alloy_counts[ing.key] || 0);
      if (have < ing.qty) return false;
    }
    return true;
  }, [data]);

  const startCraft = useCallback(async (recipe) => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/api/crafting/start', { recipe_id: recipe.id, qty: 1 });
      setData(res);
      onChanged?.();
      toast.success(`Fabricating ${recipe.name}…`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start fabrication');
    } finally { setBusy(false); }
  }, [api, onChanged]);

  const collect = useCallback(async (job) => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/api/crafting/collect', { job_id: job.id });
      setData(res);
      onChanged?.();
      toast.success(`Collected ${job.qty}× ${job.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Not ready yet');
    } finally { setBusy(false); }
  }, [api, onChanged]);

  if (!open) return null;

  const jobsFull = data && data.active_jobs >= data.max_jobs;
  const categories = data?.categories || [];

  const renderRecipe = (r) => {
    const Icon = RECIPE_ICON[r.icon] || Boxes;
    const afford = canAfford(r);
    return (
      <div key={r.id} className={`rounded-xl border p-3 ${TIER_TONE[r.tier].split(' ')[0]} bg-white/[0.03]`} data-testid={`recipe-card-${r.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-amber-300" />
            <span className="text-sm font-semibold text-slate-100">{r.name}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant="outline" className={`text-[10px] ${TIER_TONE[r.tier]}`}>T{r.tier}</Badge>
            <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(r.craft_seconds)}</span>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-1 mb-2">{r.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {r.inputs.map((ing, idx) => {
            const have = ing.type === 'ore' ? (data.ore_counts[ing.key] || 0) : (data.alloy_counts[ing.key] || 0);
            const label = ing.type === 'ore' ? ing.key : (nameById[ing.key] || ing.key);
            const ok = have >= ing.qty;
            return (
              <span
                key={idx}
                className={`text-[10px] px-1.5 py-0.5 rounded border tabular-nums ${ok ? 'border-emerald-400/30 text-emerald-200' : 'border-rose-400/40 text-rose-300'}`}
                data-testid={`recipe-input-${r.id}-${ing.key}`}
              >
                {ing.qty}× {label} <span className="opacity-60">({Math.floor(have)})</span>
              </span>
            );
          })}
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-400/25 text-amber-200/80 tabular-nums inline-flex items-center gap-1">
            <Coins className="w-3 h-3" />{r.value_per_unit}
          </span>
        </div>
        <Button
          size="sm" disabled={busy || !afford || jobsFull}
          onClick={() => startCraft(r)}
          data-testid={`recipe-craft-${r.id}`}
          className={`w-full h-7 text-xs font-semibold ${afford && !jobsFull ? 'bg-amber-400/90 hover:bg-amber-300 text-slate-900' : 'bg-white/5 text-slate-500'}`}
        >
          <Hammer className="w-3.5 h-3.5 mr-1" />
          {jobsFull ? 'Queue full' : (afford ? 'Fabricate' : 'Missing materials')}
        </Button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="crafting-overlay">
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-amber-400/25 bg-[#120d07]/95 shadow-[0_0_60px_rgba(245,180,60,0.15)] flex flex-col"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Factory className="w-5 h-5 text-amber-300" />
            <div>
              <h2 className="text-lg font-semibold text-amber-100 tracking-wide">Fabricator · Refining</h2>
              <p className="text-[11px] text-slate-400">{source === 'ship' ? 'Ship Fabricator terminal' : 'Refinery'} · refine ore into alloys</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="crafting-close-button" className="text-slate-300 hover:text-white">
            <DoorOpen className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {loading && <div className="text-center text-slate-400 py-10">Spinning up the fabricator…</div>}

          {!loading && data && (
            <>
              {/* Active jobs — compact, always visible */}
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-amber-300/80 mb-2">
                  Fabrication Queue <span className="text-slate-500">· {data.active_jobs}/{data.max_jobs}</span>
                </h3>
                {data.jobs.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No active jobs. Pick a recipe below.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {data.jobs.map((j) => (
                      <div key={j.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`craft-job-${j.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-100">{j.qty}× {j.name}</span>
                          <Badge variant="outline" className={`text-[10px] ${TIER_TONE[j.tier]}`}>T{j.tier}</Badge>
                        </div>
                        {j.ready ? (
                          <Button
                            size="sm" disabled={busy} onClick={() => collect(j)}
                            data-testid={`craft-collect-${j.id}`}
                            className="w-full mt-2 h-7 bg-emerald-500/90 hover:bg-emerald-400 text-slate-900 font-semibold text-xs"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Collect
                          </Button>
                        ) : (
                          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-amber-200/90 tabular-nums">
                            <Clock className="w-3.5 h-3.5" /> {fmtTime(j.remaining)} remaining
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Tabbed recipe catalog (by category) + Stock */}
              <Tabs defaultValue={categories[0]?.id || 'metals'} className="w-full">
                <TabsList className="flex flex-wrap h-auto bg-white/5 gap-1 p-1">
                  {categories.map((c) => {
                    const CIcon = CAT_ICON[c.icon] || Boxes;
                    return (
                      <TabsTrigger
                        key={c.id} value={c.id}
                        data-testid={`craft-tab-${c.id}`}
                        className="text-slate-300 data-[state=active]:bg-amber-400/20 data-[state=active]:text-amber-100"
                      >
                        <CIcon className="w-3.5 h-3.5 mr-1" />{c.label}
                      </TabsTrigger>
                    );
                  })}
                  <TabsTrigger
                    value="stock" data-testid="craft-tab-stock"
                    className="text-slate-300 data-[state=active]:bg-emerald-400/20 data-[state=active]:text-emerald-100"
                  >
                    <Boxes className="w-3.5 h-3.5 mr-1" />Stock
                  </TabsTrigger>
                </TabsList>

                {categories.map((c) => (
                  <TabsContent key={c.id} value={c.id}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {data.recipes
                        .filter((r) => r.category === c.id)
                        .sort((a, b) => a.tier - b.tier)
                        .map(renderRecipe)}
                    </div>
                  </TabsContent>
                ))}

                <TabsContent value="stock">
                  {(data.alloys || []).length === 0 ? (
                    <p className="text-xs text-slate-500 italic py-4">No alloys yet — fabricate some, then sell them at a Trade Terminal.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="alloy-stock">
                      {data.alloys.map((a) => (
                        <div key={a.id} className="flex items-center justify-between rounded-lg border border-emerald-400/25 bg-emerald-400/5 px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] ${TIER_TONE[a.tier] || ''}`}>T{a.tier}</Badge>
                            <span className="text-sm text-emerald-100">{a.name}</span>
                          </div>
                          <div className="text-[11px] text-emerald-200/80 tabular-nums flex items-center gap-2">
                            <span>×{Math.floor(a.qty)}</span>
                            <span className="inline-flex items-center gap-0.5 text-amber-200/80"><Coins className="w-3 h-3" />{a.value_per_unit}</span>
                          </div>
                        </div>
                      ))}
                      <p className="sm:col-span-2 text-[11px] text-slate-500 italic mt-1">Sell alloys for credits at a space-station or placeable Trade Terminal.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
